import {useState} from 'react';
import {m} from 'framer-motion';
import {slugify} from 'src/utils/change-case';
import {Box, Typography, CardActionArea, Paper} from '@mui/material';
import {alpha} from '@mui/material/styles';

import {RouterLink} from 'src/routes/components';

import Image from 'src/components/image';
import Label from 'src/components/label';
import {varHover, varTranHover} from 'src/components/animate';

// ----------------------------------------------------------------------

type Props = {
  item: {
    name: string;
    description?: string;
    icon: string;
    href: string;
    category?: string;
  };
};

export default function ComponentCard({item}: Props) {
  const {name, description, href, category} = item;
  const [isHovered, setIsHovered] = useState(false);

  const normalIconPath = `/logos/apps/${slugify(name)}.svg`;
  const hoverIconPath = `/logos/apps/${slugify(name)}-hover.svg`;

  const defaultIconPath = '/logos/apps/bytepaddemo.svg';
  const defaultHoverIconPath = `/logos/apps/bytepaddemo-hover.svg`;

  return (
    <Paper
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      component={RouterLink}
      href={href}
      variant="outlined"
      sx={{
        overflow: 'hidden',
        position: 'relative',
        textDecoration: 'none',
        borderRadius: 1,
        borderWidth: 2,
        borderColor: theme => alpha(theme.palette.grey[500], 0.08),
        height: '280px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: theme => alpha(theme.palette.grey[500], 0.15)
      }}>
      {category && (
        <Label
          color={category === 'NEW' ? 'info' : 'default'}
          sx={{
            top: 4,
            right: 4,
            zIndex: 9,
            position: 'absolute'
          }}>
          {category}
        </Label>
      )}

      <CardActionArea
        component={m.div}
        whileHover="hover"
        sx={{
          bgcolor: theme => alpha(theme.palette.grey[500], 0.04),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flexGrow: 1,
          color: '#919EAB',
          '&:hover': {
            color: '#06D6A0'
          }
        }}>
        <Box
          sx={{
            height: '120px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          <m.div variants={varHover(1.1)} transition={varTranHover()}>
            <Image
              alt={name}
              src={isHovered ? hoverIconPath : normalIconPath}
              onError={e => {
                (e.target as HTMLImageElement).src = isHovered
                  ? defaultHoverIconPath
                  : defaultIconPath;
              }}
            />
          </m.div>
        </Box>
      </CardActionArea>

      <Box
        sx={{
          textAlign: 'center',
          width: '100%',
          padding: 1,
          bgcolor: 'white',
          height: '70px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
        <Typography variant="subtitle1" sx={{fontWeight: 'bold'}}>
          {name}
        </Typography>
        {description && (
          <Typography
            sx={{color: 'text.secondary', fontSize: '12px !important'}}>
            {description}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
